import React from 'react';
import styled from 'styled-components';
import { Card, Typography, Row } from 'antd';
import {
  EditOutlined,
  DeleteFilled,
  LockOutlined,
  UnlockOutlined,
} from '@ant-design/icons';
import { FONT_WEIGHTS, COLORS, TEXT_SIZES } from '../../../utils/constants';
import { SlotDto } from '../../../types/types';
import { formatToHHMM, utcToLocalDate } from '../../../utils/dateUtils';
import dayjs from 'dayjs';
import { SlotStatus } from '../../../enums/enums';

interface SlotCardProps {
  slot: SlotDto;
  onEdit?: (slotId: number) => void;
  onDelete?: (slotId: number) => void;
  onConfirm?: (slotId: number, expireTime: string, reserve: boolean) => void;
}

const SlotCard: React.FC<SlotCardProps> = ({
  slot,
  onEdit,
  onDelete,
  onConfirm,
}) => {
  const isDisabled =
    dayjs(slot.startTime).isBefore(dayjs()) ||
    dayjs(slot.endTime).isBefore(dayjs());
  return (
    <>
      <StyledCard>
        <Typography.Text>
          {formatToHHMM(utcToLocalDate(slot.startTime))} -{' '}
          {formatToHHMM(utcToLocalDate(slot.endTime))}
        </Typography.Text>
        <Row justify="space-between">
          <CourtName>{slot.courtName}</CourtName>
          <StyledIconWrapper $disabled={isDisabled}>
            {slot.slotStatus === SlotStatus.Available && (
              <StyledEditOutlined onClick={() => onEdit?.(slot.slotId)} />
            )}

            {slot.slotStatus === SlotStatus.Available && (
              <StyledLockOutlined
                onClick={() => onConfirm?.(slot.slotId, slot.endTime, true)}
              />
            )}

            {slot.slotStatus === SlotStatus.Reserved && (
              <StyledUnlockOutlined
                onClick={() => onConfirm?.(slot.slotId, slot.endTime, false)}
              />
            )}

            {slot.slotStatus === SlotStatus.Available && (
              <StyledDeleteFilled onClick={() => onDelete?.(slot.slotId)} />
            )}
          </StyledIconWrapper>
        </Row>
      </StyledCard>
    </>
  );
};

const StyledCard = styled(Card)`
  background-color: ${COLORS.skyBlue.secondary};
  font-weight: ${FONT_WEIGHTS.MEDIUM};
  flex: 0 1 calc((100% - (3 * 16px)) / 4);
  min-width: 150px;
  min-height: 66px;
  box-sizing: border-box;
  border: 1px solid ${COLORS.skyBlue.primary};

  .ant-card-body {
    color: ${COLORS.skyBlue.primary};
    padding: 12px 16px;
    display: flex;
    flex-direction: column;
  }
`;

const StyledIconWrapper = styled.span<{ $disabled?: boolean }>`
  cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
  pointer-events: ${(props) => (props.$disabled ? 'none' : 'auto')};
  display: flex;
  gap: 8px;
`;

const StyledEditOutlined = styled(EditOutlined)`
  font-size: ${TEXT_SIZES.BASE}px;
`;

const StyledLockOutlined = styled(LockOutlined)`
  font-size: ${TEXT_SIZES.BASE}px;
`;

const StyledUnlockOutlined = styled(UnlockOutlined)`
  font-size: ${TEXT_SIZES.BASE}px;
`;

const StyledDeleteFilled = styled(DeleteFilled)`
  font-size: ${TEXT_SIZES.BASE}px;
`;

const CourtName = styled.p`
  font-size: ${TEXT_SIZES.XXS};
  color: ${COLORS.grey.secondary};
  margin-bottom: 0px;
  font-weight: ${FONT_WEIGHTS.REGULAR};
`;

export default SlotCard;
